import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOptionalAuthContext } from '../contexts/AuthContext';
import {
  inviteAcceptApi,
  type AcceptErrorCode,
  type InvitationLookup,
} from '../services/inviteAccept';

/**
 * Account-invitation landing route (tasks 635, 731), reached from the emailed
 * accept URL `${INNOVATE_APP_URL}/accept-invite?token=…`.
 *
 * This page used to sit behind the AuthGate, which meant an invited person's
 * first experience of Monarch Works was a bare Keycloak sign-in form: no
 * mention of an invitation, no email prefilled, and only a small "Sign up" link
 * to notice on their own. The route is now **auth-optional**, so the page runs
 * in two passes:
 *
 *   1. **Anonymous.** Look the token up on the public lookup route and say who
 *      invited whom, to what role. Two doors: *Create your login*, which goes
 *      STRAIGHT to Keycloak's registration form with the invited email
 *      prefilled, and *Sign in* for someone who already has one. A dead token
 *      (expired, revoked, unknown, already used) explains itself right here —
 *      before any auth friction.
 *
 *      The wording is deliberate (kv, 2026-08-26). On this platform an
 *      *Account* is the tenant being JOINED; what this button creates is a
 *      Monarch Works **login**. Calling it "create your account" would name the
 *      wrong thing twice over — the heading already carries the join framing.
 *   2. **Authenticated**, arriving back from either flow (or already signed in).
 *      Post the token to the accept proxy and show the success screen naming
 *      the account and role that were joined.
 *
 * The email warning on the landing state is load-bearing, not decoration: the
 * join is keyed on the *verified* email, so creating a login under a different
 * address silently forks a separate, empty Account instead of joining the team.
 *
 * Gated by `innovate.fe.invite-accept.page` (see App.tsx / FlaggedRoute).
 */

type PageState =
  | { phase: 'loading' }
  | { phase: 'landing'; invitation: InvitationLookup }
  | { phase: 'accepting' }
  | {
      phase: 'accepted';
      invitation: InvitationLookup | null;
      alreadyAccepted: boolean;
      /** False when AMS made this an ADDITIONAL, non-default membership. */
      isDefaultAccount: boolean;
    }
  | { phase: 'error'; code: ErrorCode };

type ErrorCode = AcceptErrorCode | 'INVALID_LINK' | 'UNAVAILABLE';

const ERROR_COPY: Record<ErrorCode, { title: string; body: string }> = {
  INVALID_LINK: {
    title: 'This invitation link is incomplete',
    body: 'The link is missing its invitation code. Ask whoever invited you to resend the invitation.',
  },
  INVITATION_NOT_FOUND: {
    title: 'This invitation link is invalid',
    body: 'We couldn’t find an invitation for this link. It may have been mistyped or already withdrawn. Ask for a fresh invitation.',
  },
  EMAIL_MISMATCH: {
    title: 'This invitation was sent to a different email',
    body: 'You’re signed in with an email that doesn’t match this invitation. Sign in with the invited address, or ask for an invitation to your current email.',
  },
  EXPIRED: {
    title: 'This invitation has expired',
    body: 'Invitations lapse after 14 days. Ask whoever invited you to resend it and try again.',
  },
  REVOKED: {
    title: 'This invitation was withdrawn',
    body: 'This invitation is no longer active. Ask for a new one if you still need access.',
  },
  ALREADY_ACCEPTED: {
    title: 'This invitation was already used',
    body: 'It looks like this invitation has already been accepted. If that wasn’t you, ask for a new invitation.',
  },
  ACCOUNT_NOT_FOUND: {
    title: 'That account is no longer available',
    body: 'The account this invitation points to no longer exists. Ask whoever invited you for guidance.',
  },
  USER_NOT_FOUND: {
    title: 'We couldn’t match your account',
    body: 'Your sign-in isn’t fully set up yet. Try again in a moment, or contact whoever invited you.',
  },
  UNAVAILABLE: {
    title: 'We couldn’t complete your invitation',
    body: 'Something went wrong finishing your invitation. You may already have access — continue to your workspace, or try the link again.',
  },
};

/** Map a non-pending lookup status onto the error copy it already has. */
const STATUS_ERROR: Record<string, ErrorCode> = {
  expired: 'EXPIRED',
  revoked: 'REVOKED',
  accepted: 'ALREADY_ACCEPTED',
};

/**
 * Pull the machine code off a rejected API call.
 *
 * The shared axios instance reshapes failures: its response interceptor lifts
 * the response BODY onto `error.response`, so the code lives at
 * `err.response.code` rather than the raw axios `err.response.data.code`. Both
 * shapes are read here — the previous version of this page read only the raw
 * one, which is why every rejection landed on the neutral copy regardless of
 * what the server actually said.
 */
function errorCode(err: unknown): ErrorCode | null {
  const candidate = err as {
    response?: { code?: string; data?: { code?: string } };
    code?: string;
  };
  const code =
    candidate?.response?.code ?? candidate?.response?.data?.code ?? candidate?.code;
  return code && code in ERROR_COPY ? (code as ErrorCode) : null;
}

/** Human label for an account role slug (`account_admin` → `Admin`). */
function roleLabel(role: string): string {
  const bare = role.replace(/^account[_-]/, '').replace(/[_-]/g, ' ');
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  // Optional: the anonymous pass renders with the provider present but with no
  // session, and the component suite renders this page without a provider.
  const auth = useOptionalAuthContext();
  const isAuthenticated = auth?.isAuthenticated === true;

  const [state, setState] = useState<PageState>({ phase: 'loading' });
  // Guard the one-shot flow against StrictMode's double effect.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    // Wait for Keycloak to settle: starting the anonymous landing flow while
    // auth is still initialising would offer "create your login" to somebody
    // who is about to be recognised as already signed in.
    if (auth?.isLoading) return;
    startedRef.current = true;

    if (!token) {
      setState({ phase: 'error', code: 'INVALID_LINK' });
      return;
    }

    void (async () => {
      // The lookup runs in BOTH passes: anonymously it is the landing page's
      // data source, and after sign-in it is what lets the success screen name
      // the account and role rather than saying "an account".
      let invitation: InvitationLookup | null = null;
      try {
        const res = await inviteAcceptApi.lookup(token);
        invitation = res.data.data;
      } catch (err) {
        const code = errorCode(err);
        if (!isAuthenticated) {
          // Anonymous: the lookup IS the page. A hard failure has to be
          // explained here, since there is nothing else to fall back to.
          setState({ phase: 'error', code: code ?? 'UNAVAILABLE' });
          return;
        }
        // Authenticated: the accept call below is authoritative, so a missing
        // or unavailable lookup degrades to a success screen without a name.
      }

      if (!isAuthenticated) {
        if (invitation && invitation.status !== 'pending') {
          setState({ phase: 'error', code: STATUS_ERROR[invitation.status] ?? 'UNAVAILABLE' });
          return;
        }
        if (!invitation) {
          setState({ phase: 'error', code: 'UNAVAILABLE' });
          return;
        }
        setState({ phase: 'landing', invitation });
        return;
      }

      setState({ phase: 'accepting' });
      try {
        const res = await inviteAcceptApi.accept(token);
        const data = res.data?.data;
        setState({
          phase: 'accepted',
          invitation,
          alreadyAccepted: data?.alreadyAccepted === true,
          // AMS makes an existing User's newly joined membership non-default
          // (task 635). Treated as default when unknown, so the extra guidance
          // only ever appears when the server actually said so.
          isDefaultAccount:
            (data?.membership as { isDefault?: boolean } | undefined)?.isDefault !== false,
        });
      } catch (err) {
        setState({ phase: 'error', code: errorCode(err) ?? 'UNAVAILABLE' });
      }
    })();
  }, [token, isAuthenticated, auth?.isLoading]);

  const steer = useCallback(
    (action?: 'register') => {
      if (!auth || !token) return;
      auth.loginWithOptions({
        ...(action ? { action } : {}),
        ...(state.phase === 'landing' ? { loginHint: state.invitation.email } : {}),
        // Back to this exact link, token intact, so the authenticated pass can
        // complete acceptance.
        redirectUri: window.location.href,
      });
    },
    [auth, token, state]
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center gap-4 max-w-md text-center p-6">
        {(state.phase === 'loading' || state.phase === 'accepting') && (
          <>
            <div className="h-8 w-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
            <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="invite-loading">
              {state.phase === 'accepting'
                ? 'Accepting your invitation…'
                : 'Looking up your invitation…'}
            </p>
          </>
        )}

        {state.phase === 'landing' && (
          <div data-testid="invite-landing" className="flex flex-col items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900/30">
              <svg
                className="h-6 w-6 text-indigo-600 dark:text-indigo-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.8}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
            </div>

            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              <span data-testid="invite-account-name">{state.invitation.accountName}</span> has
              invited you to join as {roleLabel(state.invitation.role)}
            </h1>

            <p className="text-sm text-gray-600 dark:text-gray-400">
              The invitation was sent to{' '}
              <span className="font-medium text-gray-900 dark:text-white" data-testid="invite-email">
                {state.invitation.email}
              </span>
              .
            </p>

            <div className="mt-2 w-full">
              <p className="mb-1.5 text-sm text-gray-600 dark:text-gray-400">
                New to Monarch Works?
              </p>
              <button
                type="button"
                onClick={() => steer('register')}
                data-testid="invite-register"
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors"
              >
                Create your login
              </button>
            </div>

            <button
              type="button"
              onClick={() => steer()}
              data-testid="invite-signin"
              className="text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              Already have a login? Sign in
            </button>

            {/* Load-bearing warning: the join is keyed on the verified email, so
                a login created under a different address forks a brand-new
                empty Account instead of joining the team — silently, today. */}
            <p
              className="mt-2 text-xs text-amber-700 dark:text-amber-400"
              data-testid="invite-email-warning"
            >
              Use this email address when you create your login. Creating a login with a different
              one starts a separate, empty account instead of joining{' '}
              {state.invitation.accountName}.
            </p>
          </div>
        )}

        {state.phase === 'accepted' && (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
              <svg
                className="h-6 w-6 text-green-600 dark:text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.8}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h1
              className="text-xl font-semibold text-gray-900 dark:text-white"
              data-testid="invite-success-title"
            >
              {state.invitation
                ? `You’ve joined ${state.invitation.accountName} as ${roleLabel(state.invitation.role)}`
                : state.alreadyAccepted
                  ? 'You’re already on the team'
                  : 'You’re in'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your invitation has been accepted. You now have access to the account.
            </p>

            {/* Multi-account switching is out of scope (task 635), so an
                additional membership must be described honestly rather than
                promising the app will open into it. */}
            {!state.isDefaultAccount && (
              <p
                className="text-sm text-gray-600 dark:text-gray-400"
                data-testid="invite-additional-account"
              >
                This is in addition to the account you already have
                {state.invitation ? `, and ${state.invitation.accountName}` : ', and it'} is not
                your default — the app still opens into your original account.
              </p>
            )}

            <button
              onClick={() => navigate('/', { replace: true })}
              data-testid="invite-continue"
              className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors"
            >
              Go to your workspace
            </button>
          </>
        )}

        {state.phase === 'error' && (
          <>
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <svg
                className="h-6 w-6 text-amber-600 dark:text-amber-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.8}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <h1
              className="text-xl font-semibold text-gray-900 dark:text-white"
              data-testid="invite-error-title"
            >
              {ERROR_COPY[state.code].title}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">{ERROR_COPY[state.code].body}</p>
            {/* Anonymous visitors have no workspace to continue to — offering
                one would just bounce them into the sign-in form this page
                exists to replace. */}
            {isAuthenticated && (
              <button
                onClick={() => navigate('/', { replace: true })}
                data-testid="invite-continue"
                className="mt-2 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 transition-colors"
              >
                Continue to your workspace
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
